import { useDraft } from '@cascade/react/hooks';
import { useI18n } from '@cascade/react/i18n';
import { useUser } from '@cascade/react/user';
import { Button, Input, Text } from '@cascade/ui-library';
import {
  isSameDisplayName,
  isValidDisplayName,
  MAX_NAME_LENGTH,
  MIN_NAME_LENGTH,
} from './AccountSettings.helpers.ts';
import classes from './AccountSettings.module.scss';

export const AccountSettings = () => {
  const { translate, translateError } = useI18n();
  const { user, isSettingDisplayName, setDisplayNameError, setDisplayName } =
    useUser();

  const storedName = user?.name ?? '';

  const {
    draft: name,
    isDirty,
    setDraft: setName,
  } = useDraft(storedName, { isEqual: isSameDisplayName });

  const trimmedName = name.trim();
  const isNameValid = isValidDisplayName(trimmedName);
  const canSave = isNameValid && isDirty && !isSettingDisplayName;

  const nameFeedback =
    trimmedName.length > 0 && !isNameValid
      ? translate('settings.account.nameInvalid', {
          min: MIN_NAME_LENGTH,
          max: MAX_NAME_LENGTH,
        })
      : undefined;

  const save = () => {
    setDisplayName({ displayName: trimmedName }).catch(() => undefined);
  };

  return (
    <div className={classes['account-settings']}>
      <div className={classes.fields}>
        <Input
          label={translate('settings.account.name')}
          value={name}
          required
          disabled={isSettingDisplayName}
          feedbackLevel={nameFeedback ? 'danger' : undefined}
          feedbackText={nameFeedback}
          onChange={setName}
        />
        <Input
          label={translate('settings.account.email')}
          type='email'
          value={user?.email ?? ''}
          disabled
        />
      </div>
      {setDisplayNameError ? (
        <Text
          type='body-2'
          color='text-danger'
          role='alert'
          content={translateError(setDisplayNameError)}
        />
      ) : null}
      <Button
        label={translate('settings.account.save')}
        disabled={!canSave}
        onClick={save}
      />
    </div>
  );
};
